import isEqual from "lodash/isEqual";
import type { LanguageLike } from "./languageCode";
import { defaultLanguageCodeFromActiveLanguages } from "./languageCode";

/** Default product image when catalog/order line has no image URL. */
export const PRODUCT_PLACEHOLDER_IMAGE_URL =
  "/assets/images/placeholder-images-image_large.webp";

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z\d+\-.]*:/i.test(value) || value.startsWith("//");
}

function getBackendBaseUrl(): string | undefined {
  const api =
    (
      globalThis as typeof globalThis & {
        API?: { backendURL?: string | null };
      }
    ).API ?? undefined;
  const rawBackend = api?.backendURL;
  if (typeof rawBackend === "string" && rawBackend.trim()) {
    return trimTrailingSlash(rawBackend.trim());
  }
  return undefined;
}

/** Normalize asset URL to backend absolute URL when API returns a relative path. */
export function resolveAbsoluteAssetUrl(
  assetUrl?: string | null
): string | undefined {
  if (typeof assetUrl !== "string") {
    return undefined;
  }

  const trimmed = assetUrl.trim();
  if (!trimmed) {
    return undefined;
  }

  if (isAbsoluteUrl(trimmed)) {
    return trimmed;
  }

  const backendBaseUrl = getBackendBaseUrl();
  if (!backendBaseUrl) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${backendBaseUrl}${trimmed}`;
  }

  return `${backendBaseUrl}/${trimmed}`;
}

export function resolveProductImageUrl(
  firstFileUrl?: string | null
): string {
  return resolveAbsoluteAssetUrl(firstFileUrl) ?? PRODUCT_PLACEHOLDER_IMAGE_URL;
}

export function pf(number: number) {
  var decimals = 2;
  var pow = Math.pow(10, decimals);
  return Number(Math.round(number * pow) / pow).toFixed(decimals);
}

/** Discount line display: API totals are positive, cart adjustments are negative. */
export function formatDiscountLineAmount(amount: number): string {
  const value = Number(amount);
  if (!Number.isFinite(value) || value === 0) {
    return pf(0);
  }
  return pf(value > 0 ? -value : value);
}

// Same function in app/Webpage.php if changes then change in here too
export function getPageLocaleUrl(
  page: {
    languageCode?: string;
    url?: string;
    language?: { locale?: string; shortname?: string; language?: { shortname?: string } } | null;
  },
  _languages?: LanguageLike[]
) {
  const nestedLocale =
    (typeof page.language?.locale === "string" && page.language.locale.trim()) ||
    (typeof page.language?.shortname === "string" &&
      page.language.shortname.trim()) ||
    (typeof page.language?.language?.shortname === "string" &&
      page.language.language.shortname.trim()) ||
    "";
  const segment =
    typeof page.languageCode === "string" && page.languageCode.trim() !== ""
      ? page.languageCode.trim()
      : nestedLocale ||
        defaultLanguageCodeFromActiveLanguages(_languages) ||
        "";
  const url =
    page.url !== undefined && page.url !== null ? String(page.url) : "";
  // Homepages must be `/{locale}` not `/{locale}/` — bare `/en` (or any locale code)
  // otherwise falls through to the notFound `*` route.
  if (url === "") {
    return "/" + segment;
  }
  return "/" + segment + "/" + url.replace(/^\//, "");
}

/** Strips a trailing route placeholder such as `:id` or `:uuid` from a page URL. */
export function stripPageRouteParam(url: string): string {
  return url.replace(/:[^/]+$/, "");
}

type PageForRouteMatch = {
  pageType?: string;
  languageCode?: string;
  url?: string;
};

/** Reads the product segment from a pathname when it matches a product page template. */
export function extractProductRouteParamFromPathname(
  pathname: string,
  pages: PageForRouteMatch[],
  languages?: LanguageLike[]
): string | undefined {
  const pathParts = pathname.split("/").filter(Boolean);

  for (const page of pages) {
    if (page.pageType !== "product") {
      continue;
    }
    const template = getPageLocaleUrl(page, languages);
    const templateParts = template.split("/").filter(Boolean);
    if (templateParts.length !== pathParts.length) {
      continue;
    }

    let param: string | undefined;
    let match = true;
    for (let i = 0; i < templateParts.length; i += 1) {
      const templatePart = templateParts[i];
      const pathPart = pathParts[i];
      if (templatePart.startsWith(":")) {
        param = pathPart;
      } else if (templatePart.toLowerCase() !== pathPart.toLowerCase()) {
        match = false;
        break;
      }
    }
    if (match && param) {
      return param;
    }
  }

  return undefined;
}

type LogErrorProps = {
  name: string | any;
  message: string | any;
  stack: string | any;
  componentStack: string | any;
  href: string | any;
};
var logStack: any = [];
var logStackTimeout: any;
export function logErrorToBackend(data: LogErrorProps) {
  logStack.push(data);
  if (logStackTimeout) {
    clearTimeout(logStackTimeout);
  }
  logStackTimeout = setTimeout(() => {
    logErrorStackToBackend();
  }, 3000);
}
function logErrorStackToBackend() {
  if (logStack.length) {
    const data: any = {
      logStack: logStack,
    };
    logStack = [];
    // @ts-ignore
    window.axios
      .post("logError", data)
      .then((res: any) => {})
      .catch((error: any) => {
        console.log(error);
      });
  }
}

export const getUpdatedComponents = (
  initialStructure: { components: { [key: string]: any } },
  currentStructure: { components: { [key: string]: any } }
) => {
  const updated: { [key: string]: any }[] = [];

  const isEditorComponent = (component: any) =>
    component?.id === -11 || component?.type === "editor";

  const toCanonicalKey = (component: any, fallbackKey: string) => {
    const uuid = String(component?.uuid ?? "").trim();
    if (uuid) return uuid;
    if (component?.id != null) return String(component.id);
    return String(fallbackKey);
  };

  const buildCanonicalMap = (structure: { components: { [key: string]: any } }) => {
    const map: { [key: string]: any } = {};
    Object.entries(structure.components ?? {}).forEach(([key, component]) => {
      if (!component || isEditorComponent(component)) return;
      const canonicalKey = toCanonicalKey(component, key);
      map[canonicalKey] = component;
    });
    return map;
  };

  const initialCanonical = buildCanonicalMap(initialStructure);
  const currentCanonical = buildCanonicalMap(currentStructure);

  const existingComponentRefs = new Set<string>();
  const addRefs = (component: any, fallbackKey: string) => {
    if (!component) return;
    existingComponentRefs.add(String(fallbackKey));
    if (component.id != null) existingComponentRefs.add(String(component.id));
    const uuid = String(component.uuid ?? "").trim();
    if (uuid) existingComponentRefs.add(uuid);
  };

  Object.entries(initialCanonical).forEach(([key, component]) => addRefs(component, key));
  Object.entries(currentCanonical).forEach(([key, component]) => addRefs(component, key));

  const sanitizeRelations = (component: any) => {
    if (
      !component.childWebpageComponentRelations ||
      !Array.isArray(component.childWebpageComponentRelations)
    ) {
      return component;
    }

    const filteredRelations = component.childWebpageComponentRelations.filter(
      (relation: any) => {
        if (relation.child_id === -11) return false;

        const relationChildUuid = String(relation.child_uuid ?? "").trim();
        const relationChildId =
          relation.child_id != null ? String(relation.child_id) : "";

        if (relationChildUuid && existingComponentRefs.has(relationChildUuid)) {
          return true;
        }
        if (relationChildId && existingComponentRefs.has(relationChildId)) {
          return true;
        }
        return false;
      }
    );

    return {
      ...component,
      childWebpageComponentRelations: filteredRelations,
    };
  };

  Object.entries(currentCanonical).forEach(([canonicalKey, component]) => {
    const initialComponent = initialCanonical[canonicalKey];

    if (!initialComponent || !isEqual(initialComponent, component)) {
      updated.push(sanitizeRelations(component));
    }
  });

  return updated;
};

export const getUpdatedWebpageComponents = (
  initialStructure: Array<string | number>,
  currentStructure: Array<string | number>
) => {
  if (!Array.isArray(initialStructure) || !Array.isArray(currentStructure)) {
    console.error("Invalid input to getUpdatedWebpageComponents");
    return [];
  }

  // Check if there is any difference in the initialStructure and currentStructure
  if (!isEqual(initialStructure, currentStructure)) {
    return [...currentStructure];
  }

  return [];
};
